import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../App.css";
import { apiPostAuth } from "../../utils/api";
import toast from "react-hot-toast";

export default function CreateLoanPage() {
  const { member_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [member, setMember] = useState(null);
  const [loanAmount, setLoanAmount] = useState("");
  const [paymentTerm, setPaymentTerm] = useState(2);
  const [interestRate, setInterestRate] = useState(10);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchMemberDetail();
  }, [member_id]);

  const fetchMemberDetail = async () => {
    try {
      const response = await apiPostAuth("member/get_member_detail.php", {
        member_id,
      });

      if (response.result === 1) {
        setMember(response.member_data);
      } else {
        navigate("/dashboard/member");
      }
    } catch (error) {
      navigate("/dashboard/member");
    } finally {
      setLoading(false);
    }
  };

  const numericLoanAmount = Number(loanAmount || 0);

  const totalInterest = useMemo(() => {
    return numericLoanAmount * (Number(interestRate) / 100) * (Number(paymentTerm) - 1);
  }, [numericLoanAmount, interestRate, paymentTerm]);

  const totalPaidAmount = useMemo(() => {
    return numericLoanAmount + totalInterest;
  }, [numericLoanAmount, totalInterest]);

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!loanAmount || numericLoanAmount <= 0) {
      setErrorMessage("Please enter a valid loan amount.");
      return;
    }

    if (Number(paymentTerm) < 2) {
      setErrorMessage("Payment term must be at least 2 months.");
      return;
    }

    if (Number(interestRate) < 0) {
      setErrorMessage("Interest rate cannot be negative.");
      return;
    }

    setSubmitting(true);

    const toastId = toast.loading("Creating loan...");

    try {
      const response = await apiPostAuth("loan/create.php", {
        member_id,
        loan_amount: loanAmount,
        payment_term: paymentTerm,
        interest_rate: interestRate,
      });

      if (response.result === 1) {
        toast.success("Loan created successfully.", { id: toastId });
        navigate(`/dashboard/loan/${response.loan_id}`);
      } else {
        toast.error(response.message || "Gagal membuat Pinjaman.", { id: toastId });
        setErrorMessage(response.message || "Gagal membuat Pinjaman.");
      }
    } catch (error) {
      toast.error("Oops, something went wrong.", { id: toastId });
      setErrorMessage("Oops, something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Loading member...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate(-1)}
        style={backButtonStyle}
      >
        ← Kembali
      </button>

      <section style={sectionStyle}>
        <div style={memberHeaderStyle}>
          <img
            src={member?.profile_picture_compressed || member?.profile_picture}
            alt={member?.full_name}
            style={profileImageStyle}
          />

          <div>
            <h1 style={{ fontSize: "1.6rem", color: "#1f2937", margin: 0 }}>
              Buat Pinjaman
            </h1>

            <p style={{ margin: "6px 0 0 0", color: "#6b7280" }}>
              Member: <strong>{member?.full_name}</strong>
            </p>
          </div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Formulir Pinjaman</h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Nilai Pinjaman</label>

            <div style={currencyInputWrapperStyle}>
              <span style={currencyPrefixStyle}>Rp</span>

              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="1000000"
                style={currencyInputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: "25px" }}>
            <label style={labelStyle}>
                Durasi Pinjaman
            </label>

            <div style={{ width: "90%" }}>
                <input
                type="range"
                min="2"
                max="12"
                step="1"
                value={paymentTerm}
                onChange={(e) => setPaymentTerm(Number(e.target.value))}
                style={sliderStyle}
                />

                <div style={sliderLabelContainerStyle}>
                <span>2</span>
                <span>12</span>
                </div>

                <div style={sliderSelectedValueStyle}>
                {paymentTerm} Bulan
                </div>
            </div>
            </div>

                    <div style={{ marginBottom: "25px" }}>
            <label style={labelStyle}>
                Bunga (%)
            </label>

            <div style={{ width: "90%" }}>
                <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                style={sliderStyle}
                />

                <div style={sliderLabelContainerStyle}>
                <span>5%</span>
                <span>30%</span>
                </div>

                <div style={sliderSelectedValueStyle}>
                {interestRate}%
                </div>
            </div>
            </div>

          <div style={calculationBoxStyle}>
            <h3 style={{ marginTop: 0, color: "#1f2937" }}>Estimasi Pembayaran</h3>

            <div style={calculationRowStyle}>
              <span>Pinjaman Pokok</span>
              <strong>{formatCurrency(numericLoanAmount)}</strong>
            </div>

            <div style={calculationRowStyle}>
              <span>Bunga Dibayarkan</span>
              <strong>{formatCurrency(totalInterest)}</strong>
            </div>

            <div style={calculationTotalRowStyle}>
              <span>Total Pembayaran</span>
              <strong>{formatCurrency(totalPaidAmount)}</strong>
            </div>
          </div>

          {errorMessage && <p style={errorTextStyle}>{errorMessage}</p>}

          <button
            type="submit"
            className="primary-button"
            disabled={submitting}
            style={{ marginTop: "18px", width: "160px" }}
          >
            {submitting ? "Membuat Pinjaman..." : "Buat Pinjaman"}
          </button>
        </form>
      </section>
    </div>
  );
}

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
};

const sectionStyle = {
  backgroundColor: "#fff",
  padding: "22px",
  borderRadius: "12px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: "20px",
};

const memberHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
};

const profileImageStyle = {
  width: "70px",
  height: "70px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "3px solid #e5e7eb",
};

const sectionTitleStyle = {
  fontSize: "1.2rem",
  marginBottom: "18px",
  color: "#1f2937",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "#1f2937",
  fontWeight: "500",
};

const inputStyle = {
  width: "90%",
  padding: "10px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "1rem",
  backgroundColor: "#fff",
};

const currencyInputWrapperStyle = {
  width: "90%",
  display: "flex",
  alignItems: "center",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
};

const currencyPrefixStyle = {
  padding: "10px 14px",
  backgroundColor: "#f9fafb",
  color: "#374151",
  fontWeight: "600",
  borderRight: "1px solid #d1d5db",
};

const currencyInputStyle = {
  flex: 1,
  padding: "10px",
  border: "none",
  outline: "none",
  fontSize: "1rem",
};

const calculationBoxStyle = {
  width: "90%",
  backgroundColor: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "18px",
  marginTop: "10px",
};

const calculationRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "8px 0",
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
};

const calculationTotalRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 0 0 0",
  color: "#1f2937",
  fontSize: "1.05rem",
};

const errorTextStyle = {
  color: "#dc2626",
  fontSize: "0.9rem",
  marginTop: "12px",
};

const sliderStyle = {
  width: "100%",
  cursor: "pointer",
  accentColor: "#2563eb",
};

const sliderLabelContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "6px",
  fontSize: "0.85rem",
  color: "#6b7280",
};

const sliderSelectedValueStyle = {
  marginTop: "12px",
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  padding: "10px 14px",
  borderRadius: "10px",
  fontWeight: "700",
  fontSize: "1rem",
  textAlign: "center",
  border: "1px solid #bfdbfe",
};